import {
  GeoPlacesClient,
  AutocompleteCommand,
  GeocodeCommand,
  GetPlaceCommand,
  type AutocompleteCommandInput,
  type AutocompleteCommandOutput,
  type GeocodeCommandInput,
  type GeocodeCommandOutput,
  type GetPlaceCommandInput,
  type GetPlaceCommandOutput,
} from '@aws-sdk/client-geo-places';
import {
  GeoMapsClient,
  GetStaticMapCommand,
  StaticMapStyle,
  type GetStaticMapCommandInput,
  type GetStaticMapCommandOutput,
} from '@aws-sdk/client-geo-maps';
import { Auth } from 'aws-amplify';
import { getAuthConfig } from '../utils/requestConfig';

let placesClient: GeoPlacesClient | null = null;
let mapsClient: GeoMapsClient | null = null;

async function getCognitoCredentials(): Promise<{
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
}> {
  const creds = await Auth.currentCredentials();
  return {
    accessKeyId: creds.accessKeyId,
    secretAccessKey: creds.secretAccessKey,
    sessionToken: creds.sessionToken,
  };
}

export function getGeoPlacesClient(): GeoPlacesClient {
  if (!placesClient) {
    placesClient = new GeoPlacesClient({
      region: getAuthConfig().region,
      credentials: getCognitoCredentials,
    });
  }
  return placesClient;
}

export function getGeoMapsClient(): GeoMapsClient {
  if (!mapsClient) {
    mapsClient = new GeoMapsClient({
      region: getAuthConfig().region,
      credentials: getCognitoCredentials,
    });
  }
  return mapsClient;
}

export function autocomplete(params: AutocompleteCommandInput): Promise<AutocompleteCommandOutput> {
  return getGeoPlacesClient().send(new AutocompleteCommand(params));
}

// IntendedUse must be set to 'Storage' when persisting results (service terms requirement)
export function geocode(params: GeocodeCommandInput): Promise<GeocodeCommandOutput> {
  return getGeoPlacesClient().send(new GeocodeCommand(params));
}

export function getPlace(params: GetPlaceCommandInput): Promise<GetPlaceCommandOutput> {
  return getGeoPlacesClient().send(new GetPlaceCommand(params));
}

export function getStaticMap(params: GetStaticMapCommandInput): Promise<GetStaticMapCommandOutput> {
  return getGeoMapsClient().send(new GetStaticMapCommand(params));
}

// Caller must call URL.revokeObjectURL() on the returned URL when done
export async function getStaticMapBlobUrl(params: {
  center: [number, number];
  zoom?: number;
  width?: number;
  height?: number;
  style?: StaticMapStyle;
}): Promise<string> {
  const { center, zoom = 15, width = 600, height = 400, style = StaticMapStyle.STANDARD } = params;

  const response = await getStaticMap({
    Center: `${center[0]},${center[1]}`,
    Zoom: zoom,
    Width: width,
    Height: height,
    Style: style,
    FileName: 'map',
  });

  const blob = new Blob([new Uint8Array(response.Blob as Uint8Array)], {
    type: response.ContentType ?? 'image/png',
  });
  return URL.createObjectURL(blob);
}

export function resetClients(): void {
  placesClient = null;
  mapsClient = null;
}
